<template>
    <div class="wizard">
        <div class="wizard-mobile-overview">
            <div v-if="activeStepIndex !== 0" class="wizard-back" @click="back">
                <span class="sg-vrm-icon_048_arrow-left-16px"></span>
                Back
            </div>
            <div v-if="activeStepIndex === 0"></div>
            <div v-if="activeStepIndex < steps.length - 1">
                Next: {{ steps[activeStepIndex + 1].title }}
            </div>
        </div>
        <div class="wizard-progress">
            <step
                v-for="(step, idx) in steps"
                :key="step.id"
                :index="idx"
                :title="step.title"
                :completed-step-index="completedStepIndex"
                :active-step-index="activeStepIndex"
                :disabled="backDisabled"
                @changeStep="goToStep"
            />
        </div>
        <slot></slot>
        <div class="row">
            <div class="col-md-10 col-md-offset-1">
                <br />
                <div class="sg-spread">
                    <div v-if="!isMobile">
                        <button
                            class="sg-btn sg-site"
                            :disabled="backDisabled"
                            @click.prevent="back"
                        >
                            {{ $t('general.back', { i18nFallback: 'Back' }) }}
                        </button>
                        <button
                            class="back-to-overview"
                            :disabled="backDisabled"
                            @click.prevent="resetWithChangeNotification"
                        >
                            {{ $t('ess.back_to_overview', { i18nFallback: 'Back to overview' }) }}
                        </button>
                    </div>
                    <button
                        v-if="isMobile"
                        class="sg-btn sg-site"
                        :disabled="backDisabled"
                        @click.prevent="back"
                    >
                        {{ $t('general.back', { i18nFallback: 'Back' }) }}
                    </button>
                    <button
                        v-if="activeStepIndex === steps.length - 1 || canSubmit"
                        class="sg-btn sg-blue"
                        :disabled="nextDisabled || !hasChange"
                        @click.prevent="submit"
                    >
                        {{ $t('common.submit') }}
                    </button>
                    <button
                        v-else
                        class="sg-btn sg-blue"
                        :disabled="nextDisabled"
                        @click.prevent="next"
                    >
                        {{ $t('ess.next_step') }}
                    </button>
                </div>
                <button
                    v-if="isMobile"
                    class="back-to-overview"
                    :disabled="backDisabled"
                    @click.prevent="resetWithChangeNotification"
                >
                    {{ $t('ess.back_to_overview', { i18nFallback: 'Back to overview' }) }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Step from './Step.vue';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import ChangeUnsaveNotificationModal from '@/vue/components/modals/ChangeUnsaveNotificationModal.vue';
import { ngInject } from '@/vue/decorators/angular-interop';

@Component({
    components: {
        ChangeUnsaveNotificationModal,
        Step,
    },
})
@AngularLocaleBundle('common')
@AngularLocaleBundle('general')
@AngularLocaleBundle('ess')
export default class Wizard extends Vue {
    @Prop({ type: Array, default: () => [] })
    steps;
    @Prop({ type: String })
    value;
    @Prop({ type: Boolean, default: false })
    nextDisabled;
    @Prop({ type: Boolean })
    isEdit;
    @Prop({ type: Number })
    completedUntil;
    // prop to show submit button instead of next button
    @Prop({ type: Boolean, default: false })
    canSubmit;
    @Prop({ type: Boolean })
    hasChange;
    @Prop({ type: Boolean })
    creating;

    @ngInject()
    VrmModalService;

    activeStepIndex = 0;
    completedStepIndex = 0;
    backDisabled = false;
    isMobile = false;

    mounted() {
        this.updateActiveStepIndex();
        this.updateCompletedStepIndex();
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        window.addEventListener('resize', this.handleResize);
    }

    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    handleResize() {
        this.isMobile = window.innerWidth <= 750;
    }

    @Watch('nextDisabled')
    nextDisabledChanged() {
        this.backDisabled = this.nextDisabled && this.isEdit;
    }

    @Watch('steps')
    updateCompletedStepIndex() {
        // if edit mode, set completed step index to last step
        if (this.isEdit) {
            this.completedStepIndex = this.completedUntil;
            return;
        }
        if (this.activeStepIndex > this.completedStepIndex) {
            this.completedStepIndex = this.activeStepIndex;
        }
    }

    @Watch('value')
    updateActiveStepIndex() {
        // find index of active step when value changes
        this.activeStepIndex = this.steps.findIndex(step => step.id === this.value);
        this.updateCompletedStepIndex();
    }

    submit() {
        this.$emit('submit', { closure: this.activeStepIndex === this.steps.length - 1 });
    }

    goToStep(index) {
        if (this.hasChange && !this.creating) {
            this.VrmModalService.openVueModal(ChangeUnsaveNotificationModal, {
                callbackSave: this.submit,
                callbackReset: this.resetForm,
            });
        }
        this.$emit('input', this.steps[index].id);
    }

    back() {
        if (this.activeStepIndex === 0) {
            this.resetWithChangeNotification();
        }
        this.goToStep(this.activeStepIndex - 1);
    }

    submitAndReset() {
        this.$emit('submitAndReset', { closure: this.activeStepIndex === this.steps.length - 1 });
    }

    resetForm() {
        this.$emit('resetForm');
    }

    resetFormAndGoBack() {
        // need to fetch data again to discard changes
        this.resetForm();
        this.$emit('reset');
    }

    next() {
        this.$emit('input', this.steps[this.activeStepIndex + 1].id);
    }

    handleBeforeUnload(event) {
        if (this.hasChange) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    resetWithChangeNotification() {
        if (this.hasChange && !this.creating) {
            this.VrmModalService.openVueModal(ChangeUnsaveNotificationModal, {
                callbackSave: this.submitAndReset,
                callbackReset: this.resetFormAndGoBack,
            });
            return;
        }
        if (this.creating) {
            this.resetForm();
        }
        this.$emit('reset');
        return;
    }
}
</script>
